import React from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { Button, Card, Modal, Portal, Text } from 'react-native-paper';
import { colors, spacing, typography } from '../../../constants/theme';
import { ReviewTemplate, Vendor } from '../AdminDashboard.types';

interface AdminVendorModalProps {
  visible: boolean;
  template: ReviewTemplate | null;
  availableVendors: Vendor[];
  loading: boolean;
  onDismiss: () => void;
  onToggleVendorAssociation: (vendorId: number, isAssociated: boolean) => void;
}

export const AdminVendorModal: React.FC<AdminVendorModalProps> = ({
  visible,
  template,
  availableVendors,
  loading,
  onDismiss,
  onToggleVendorAssociation,
}) => (
  <Portal>
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      contentContainerStyle={styles.modal}
    >
      <ScrollView>
        <Text style={styles.modalTitle}>
          Manage Vendors for: {template?.name}
        </Text>

        {loading ? (
          <View style={styles.loadingVendors}>
            <Text>Loading vendors...</Text>
          </View>
        ) : (
          <>
            <Text style={styles.sectionTitle}>
              Available Vendors ({availableVendors.length})
            </Text>

            {availableVendors.length === 0 ? (
              <Text style={styles.emptyVendors}>
                No vendors available. Create vendors first in the Vendors section.
              </Text>
            ) : (
              availableVendors
                .filter(vendor => vendor.isActive)
                .map(vendor => {
                  const isAssociated = template?.vendors?.some(
                    existingVendor => existingVendor.id === vendor.id
                  ) || false;

                  return (
                    <Card key={vendor.id} style={[
                      styles.vendorCard,
                      isAssociated && styles.associatedVendorCard,
                    ]}>
                      <Card.Content>
                        <View style={styles.vendorCardHeader}>
                          <View style={styles.vendorCardInfo}>
                            <Text style={styles.vendorCardName}>{vendor.name}</Text>
                            {vendor.vendorTypes && vendor.vendorTypes.length > 0 && (
                              <Text style={styles.vendorCardTypes}>
                                {vendor.vendorTypes.map(type => type.name).join(', ')}
                              </Text>
                            )}
                            {vendor.email && (
                              <Text style={styles.vendorCardDetail}>📧 {vendor.email}</Text>
                            )}
                          </View>
                          <Button
                            mode={isAssociated ? 'contained' : 'outlined'}
                            onPress={() => onToggleVendorAssociation(vendor.id, isAssociated)}
                            style={styles.vendorToggleButton}
                          >
                            {isAssociated ? 'Remove' : 'Add'}
                          </Button>
                        </View>
                      </Card.Content>
                    </Card>
                  );
                })
            )}

            <Button
              mode="outlined"
              onPress={onDismiss}
              style={styles.modalCloseButton}
            >
              Close
            </Button>
          </>
        )}
      </ScrollView>
    </Modal>
  </Portal>
);

const styles = StyleSheet.create({
  modal: {
    backgroundColor: colors.background,
    margin: spacing.lg,
    padding: spacing.lg,
    borderRadius: 8,
    maxHeight: '90%',
    minHeight: '70%',
  },
  modalTitle: {
    ...typography.h2,
    marginBottom: spacing.lg,
  },
  sectionTitle: {
    ...typography.h2,
    marginBottom: spacing.md,
  },
  loadingVendors: {
    padding: spacing.lg,
    alignItems: 'center',
  },
  emptyVendors: {
    ...typography.body,
    color: colors.textSecondary,
    textAlign: 'center',
    padding: spacing.lg,
    fontStyle: 'italic',
  },
  vendorCard: {
    marginBottom: spacing.sm,
    backgroundColor: colors.surface,
  },
  associatedVendorCard: {
    backgroundColor: colors.primary + '10',
    borderLeftWidth: 4,
    borderLeftColor: colors.primary,
  },
  vendorCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  vendorCardInfo: {
    flex: 1,
    marginRight: spacing.md,
  },
  vendorCardName: {
    ...typography.h4,
    marginBottom: spacing.xs,
  },
  vendorCardTypes: {
    ...typography.caption,
    color: colors.primary,
    fontWeight: '600',
    marginBottom: spacing.xs,
  },
  vendorCardDetail: {
    ...typography.caption,
    color: colors.textSecondary,
  },
  vendorToggleButton: {
    minWidth: 80,
  },
  modalCloseButton: {
    marginTop: spacing.lg,
  },
});

export default AdminVendorModal;
